import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
    getMaxDevicesConfig,
    updateMaxDevicesConfig,
} from "../../services/UserAdminService";

/**
 * Card chỉnh sửa giới hạn số thiết bị đăng nhập song song.
 * Chỉ Admin (role=1) được phép cập nhật.
 */
function MaxDevicesCard() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [value, setValue] = useState("");
    const [saved, setSaved] = useState(null);
    const [range, setRange] = useState({ min: 1, max: 20 });

    const load = async () => {
        setLoading(true);
        try {
            const res = await getMaxDevicesConfig();
            const cfg = res?.data || {};
            setSaved(cfg.MAX_DEVICES);
            setValue(String(cfg.MAX_DEVICES ?? ""));
            if (typeof cfg.min === "number" && typeof cfg.max === "number") {
                setRange({ min: cfg.min, max: cfg.max });
            }
        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                err.message ||
                "Không tải được cấu hình",
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const parsed = parseInt(value, 10);
    const isInvalid =
        value === "" ||
        Number.isNaN(parsed) ||
        parsed < range.min ||
        parsed > range.max;
    const isDirty = !isInvalid && parsed !== saved;

    const handleSave = async () => {
        if (saving || isInvalid || !isDirty) return;
        setSaving(true);
        try {
            const res = await updateMaxDevicesConfig(parsed);
            const newVal = res?.data?.MAX_DEVICES ?? parsed;
            setSaved(newVal);
            setValue(String(newVal));
            toast.success(res?.message || "Đã cập nhật");
        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                err.message ||
                "Cập nhật thất bại",
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5">
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                    <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.8}
                            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                    </svg>
                </div>

                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900">
                        Giới hạn thiết bị đăng nhập
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                        Số thiết bị tối đa một tài khoản được phép đăng nhập đồng thời.
                        Hiện tại:{" "}
                        <span className="font-semibold text-gray-800">
                            {loading ? "…" : (saved ?? "—")}
                        </span>
                    </p>

                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <input
                            type="number"
                            min={range.min}
                            max={range.max}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            disabled={loading || saving}
                            className={`w-24 px-3 py-2 text-sm border rounded-lg outline-none ${value !== "" && isInvalid
                                    ? "border-red-500 focus:border-red-500"
                                    : "border-gray-300 focus:border-red-600"
                                }`}
                        />
                        <span className="text-xs text-gray-400">
                            (từ {range.min} đến {range.max})
                        </span>

                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={loading || saving || isInvalid || !isDirty}
                            className="ml-auto px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {saving ? "Đang lưu…" : "Lưu thay đổi"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MaxDevicesCard;
